import clsx from 'clsx'
import {
  type DetailedHTMLProps,
  type ForwardedRef,
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react'

import { Icon } from '@fv/client-components'

import { ShipperTooltip } from '@/components/ShipperTooltip'

import { Pinline } from '../Pinline'

interface CheckboxFieldProps
  extends DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  className?: string
  hint?: ReactNode
  label?: ReactNode
  labelClassName?: string
  name: string
  tooltipOnOverflow?: boolean
  alternate?: boolean
  boxedIn?: boolean
  boxedInHelp?: string
  stopPropagation?: boolean
}

export const CheckboxField = forwardRef(
  (
    {
      className = '',
      hint,
      label,
      labelClassName,
      name,
      tooltipOnOverflow = false,
      alternate,
      boxedIn,
      boxedInHelp,
      stopPropagation = false,
      ...rest
    }: CheckboxFieldProps,
    ref?: ForwardedRef<HTMLInputElement>,
  ) => {
    const labelContent = label && (
      <label
        className={clsx(
          'relative peer-disabled:cursor-not-allowed peer-disabled:opacity-75 whitespace-nowrap ml-2 overflow-hidden text-ellipsis',
          labelClassName,
        )}
        htmlFor={name}
      >
        {label}
      </label>
    )

    return (
      <div
        className={`flex items-center min-w-0 relative ${className} ${boxedIn ? 'bg-white border-fv-gray border p-2 !pr-10' : ''}`}
      >
        <input
          className="peer opacity-0 absolute"
          id={name}
          name={name}
          ref={ref}
          type="checkbox"
          {...rest}
        />

        <label
          className={clsx(
            'w-[26px] h-[26px] cursor-pointer relative border-2 border-fv-blue peer-checked:after:opacity-100 after:absolute after:top-1/2 after:left-1/2 after:w-[14px] after:h-[14px] after:bg-fv-gray-dark after:opacity-0 after:-translate-x-1/2 after:-translate-y-1/2 after:transition-opacity after:duration-200 after:ease-linear peer-focus:border-fv-gray-dark peer-disabled:cursor-not-allowed peer-disabled:opacity-75 flex-none',
            alternate ? 'bg-white' : 'bg-fv-blue-50',
          )}
          htmlFor={name}
          onPointerDown={stopPropagation ? e => e.stopPropagation() : undefined}
        />

        {labelContent &&
          (tooltipOnOverflow && typeof label === 'string' ? (
            <ShipperTooltip label={label} onlyOnOverflow>
              {labelContent}
            </ShipperTooltip>
          ) : (
            labelContent
          ))}

        {hint}
        {boxedInHelp && (
          <>
            <Pinline className="mx-2" dashed flex />
            <ShipperTooltip label={boxedInHelp}>
              <div className={clsx('ml-auto flex items-center')}>
                <Icon icon="question-circle" className="text-fv-orange" />
              </div>
            </ShipperTooltip>
          </>
        )}
      </div>
    )
  },
)

export default CheckboxField
